import { createApp } from '@backstage/frontend-defaults';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { SignInPage, type IdentityProviders } from '@backstage/core-components';
import { googleAuthApiRef } from '@backstage/core-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import orgPlugin from '@backstage/plugin-org/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import techdocsAddons from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import techInsightsPlugin from '@backstage-community/plugin-tech-insights/alpha';
import announcementsPlugin from '@backstage-community/plugin-announcements/alpha';
import appsMonitorPlugin from '@internal/plugin-apps-monitor';
import {
  apiDocsConfigRef,
  defaultDefinitionWidgets,
  OpenApiDefinitionWidget,
} from '@backstage/plugin-api-docs';
import { navModule } from './modules/nav';
import { themeModule } from './modules/theme';

// Sign-in providers. Google is the only provider in the deployed app.
// (New frontend system: the sign-in page is an extension, not JSX in the app root.)
const googleProvider = {
  id: 'google-auth-provider',
  title: 'Google',
  message: 'Sign in using your Golden Hippo account',
  apiRef: googleAuthApiRef,
};

// Guest sign-in is offered ONLY in local development so developers can skip
// Google SSO. `process.env.NODE_ENV` is 'production' in the built image, so the
// guest option is compiled out of the deployed bundle. Defense in depth: the
// backend guest provider also refuses to issue tokens when auth.environment is
// 'production' (set in app-config.production.yaml).
const signInProviders: IdentityProviders =
  process.env.NODE_ENV !== 'production'
    ? ['guest', googleProvider]
    : [googleProvider];

const signInPageModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: {
        loader: async () => props =>
          (
            <SignInPage
              {...props}
              title="Select a sign-in method"
              align="center"
              providers={signInProviders}
            />
          ),
      },
    }),
  ],
});

// Docs-only OpenAPI rendering: disable Swagger UI "Try it out". These specs'
// servers point at api-*.goldenhippo.com, which don't send CORS headers for the
// portal origin, so Execute always errored — and this is a read-only docs
// portal. supportedSubmitMethods: [] removes the interactive request UI.
const apiDocsReadOnly = apiDocsPlugin.withOverrides({
  extensions: [
    apiDocsPlugin.getExtension('api:api-docs/config').override({
      params: defineParams =>
        defineParams({
          api: apiDocsConfigRef,
          deps: {},
          factory: () => {
            const widgets = defaultDefinitionWidgets().map(widget =>
              widget.type === 'openapi'
                ? {
                    ...widget,
                    component: (definition: string) => (
                      <OpenApiDefinitionWidget
                        definition={definition}
                        supportedSubmitMethods={[]}
                      />
                    ),
                  }
                : widget,
            );
            return {
              getApiDefinitionWidget: apiEntity =>
                widgets.find(d => d.type === apiEntity.spec.type),
            };
          },
        }),
    }),
  ],
});

export default createApp({
  features: [
    catalogPlugin,
    catalogGraphPlugin,
    apiDocsReadOnly,
    orgPlugin,
    techdocsPlugin,
    techdocsAddons,
    techInsightsPlugin,
    announcementsPlugin,
    appsMonitorPlugin,
    navModule,
    themeModule,
    signInPageModule,
  ],
});
